import { useState } from "react";
import { CalendarPlus, Play, Radio } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useLang } from "@/context/LanguageContext";
import { Reveal, Overline } from "@/components/Reveal";
import { SERMONS } from "@/data/sermons";

const CHANNEL_ID = process.env.REACT_APP_YOUTUBE_CHANNEL_ID;

export default function Sermones() {
  const { t, lang } = useLang();
  const [active, setActive] = useState(null);
  const sermons = SERMONS;

  return (
    <div data-testid="sermones-page">
      <section className="pt-40 pb-20 max-w-7xl mx-auto px-6 md:px-10">
        <Reveal>
          <Overline>{t.sermones.overline}</Overline>
          <h1 className="font-display text-5xl md:text-7xl font-light tracking-tight mt-6">
            {t.sermones.title}
          </h1>
          <p className="text-white/60 text-lg mt-8 max-w-xl leading-relaxed">{t.sermones.sub}</p>
        </Reveal>
      </section>

      {/* Live stream */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 pb-24" data-testid="live-section">
        <Reveal>
          <div className="border border-white/10 bg-coal overflow-hidden">
            {CHANNEL_ID ? (
              <div className="aspect-video">
                <iframe
                  src={`https://www.youtube.com/embed/live_stream?channel=${CHANNEL_ID}`}
                  title={t.sermones.liveTitle}
                  className="w-full h-full"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2">
                <div className="p-10 md:p-16">
                  <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-white/50 mb-6">
                    <Radio size={14} className="text-ember" />
                    {t.sermones.liveTitle}
                  </span>
                  <h2 className="font-display text-4xl md:text-5xl font-light mb-6">
                    {t.sermones.liveOffline}
                  </h2>
                  <p className="text-white/60 leading-relaxed mb-10">{t.sermones.liveOfflineText}</p>
                  <a
                    data-testid="live-calendar-cta"
                    href="https://calendar.google.com/calendar/render?action=TEMPLATE&text=Movimiento%20Church%20Live&details=Servicio%20dominical%20en%20vivo&dates=20260719T153000Z/20260719T170000Z&recur=RRULE:FREQ=WEEKLY"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group inline-flex items-center gap-3 border border-white/30 text-white text-xs uppercase tracking-[0.2em] rounded-full px-8 py-4 hover:border-ember hover:text-ember transition-colors duration-300"
                  >
                    <CalendarPlus size={16} />
                    {t.sermones.liveCta}
                  </a>
                </div>
                <div className="relative min-h-64">
                  <img
                    src="https://images.unsplash.com/photo-1470229722913-7c0e2dbbafd3?auto=format&fit=crop&w=1600&q=80"
                    alt=""
                    className="absolute inset-0 w-full h-full object-cover opacity-60"
                  />
                  <div className="absolute inset-0 bg-gradient-to-r from-coal to-transparent" />
                </div>
              </div>
            )}
          </div>
        </Reveal>
      </section>

      {/* Sermon grid */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 pb-24 md:pb-32">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {sermons.map((s, i) => (
            <Reveal key={s.id} delay={(i % 3) * 0.08}>
              <button
                className="group w-full text-left border border-white/10 bg-coal overflow-hidden hover:-translate-y-1 transition-transform duration-500"
                onClick={() => setActive(s)}
                data-testid={`sermon-card-${s.id}`}
              >
                <div className="relative overflow-hidden aspect-video">
                  <img
                    src={s.thumb}
                    alt={s.title[lang]}
                    className="w-full h-full object-cover group-hover:scale-105 group-hover:opacity-70 transition-all duration-700"
                  />
                  <span className="absolute inset-0 flex items-center justify-center">
                    <span className="w-14 h-14 rounded-full bg-ember flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <Play size={20} className="text-white ml-0.5" />
                    </span>
                  </span>
                </div>
                <div className="p-6">
                  <p className="text-xs uppercase tracking-[0.2em] text-sand mb-2">
                    {s.series[lang]} · {s.duration}
                  </p>
                  <h3 className="font-display text-2xl font-light mb-2">{s.title[lang]}</h3>
                  <p className="text-sm text-white/50">{s.speaker[lang]} · {s.date}</p>
                </div>
              </button>
            </Reveal>
          ))}
        </div>
      </section>

      <Dialog open={!!active} onOpenChange={() => setActive(null)}>
        <DialogContent className="bg-coal border-white/10 text-white max-w-3xl" data-testid="sermon-modal">
          {active && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display text-3xl font-light text-white">
                  {active.title[lang]}
                </DialogTitle>
              </DialogHeader>
              {active.youtube_id ? (
                <div className="aspect-video">
                  <iframe
                    src={`https://www.youtube.com/embed/${active.youtube_id}`}
                    title={active.title[lang]}
                    className="w-full h-full"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                </div>
              ) : (
                <div className="relative aspect-video overflow-hidden">
                  <img src={active.thumb} alt="" className="absolute inset-0 w-full h-full object-cover opacity-40" />
                  <div className="absolute inset-0 flex flex-col items-center justify-center text-center p-8">
                    <p className="text-xs uppercase tracking-[0.25em] text-sand mb-3">{t.sermones.soon}</p>
                    <p className="text-white/70 max-w-sm">{t.sermones.soonText}</p>
                  </div>
                </div>
              )}
              <p className="text-sm text-white/60 leading-relaxed">{active.description[lang]}</p>
              <p className="text-xs uppercase tracking-[0.2em] text-white/40">
                {active.speaker[lang]} · {active.series[lang]} · {active.date}
              </p>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
